import React, { useState, useEffect } from 'react';
import { 
  Award, 
  Database, 
  Search, 
  MapPin, 
  ChevronRight, 
  X, 
  Calendar, 
  DollarSign, 
  Building, 
  Sparkles,
  Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { db, collection, onSnapshot, query, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../AuthContext';
import { COMPLETE_NAICS_CODES } from '../utils/naics';
import { MultiSelectCombo } from './MultiSelectCombo';

interface Contract {
  id: string;
  contractId: string;
  title: string;
  agency: string;
  awardee: string;
  value: number;
  description: string;
  winningProposalExcerpt: string;
  state: string;
  naics: string;
  effectiveDate: string;
  expirationDate: string;
}

const STATES_LIST = [
  { name: 'Alabama', code: 'AL' }, { name: 'Alaska', code: 'AK' }, { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' }, { name: 'California', code: 'CA' }, { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' }, { name: 'Delaware', code: 'DE' }, { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' }, { name: 'Hawaii', code: 'HI' }, { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' }, { name: 'Indiana', code: 'IN' }, { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' }, { name: 'Kentucky', code: 'KY' }, { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' }, { name: 'Maryland', code: 'MD' }, { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' }, { name: 'Minnesota', code: 'MN' }, { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' }, { name: 'Montana', code: 'MT' }, { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' }, { name: 'New Hampshire', code: 'NH' }, { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' }, { name: 'New York', code: 'NY' }, { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' }, { name: 'Ohio', code: 'OH' }, { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' }, { name: 'Pennsylvania', code: 'PA' }, { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' }, { name: 'South Dakota', code: 'SD' }, { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' }, { name: 'Utah', code: 'UT' }, { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' }, { name: 'Washington', code: 'WA' }, { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' }, { name: 'Wyoming', code: 'WY' }
];

export const StateContractsDatabaseSection: React.FC = () => {
  const { user, vendorProfile } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(true);

  // States for search and filter
  const [contractSearch, setContractSearch] = useState('');
  const [contractStates, setContractStates] = useState<string[]>([]);
  const [contractNaicsCodes, setContractNaicsCodes] = useState<string[]>([]);

  // Active / Selected Contract (persisted to localStorage)
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);

  // Modal / Detail drawer states
  const [viewingContract, setViewingContract] = useState<Contract | null>(null);

  useEffect(() => {
    // Read pre-selected contract from localStorage if any
    const savedId = localStorage.getItem('rfx_selected_contract_id');
    if (savedId) {
      setSelectedContractId(savedId);
    }
  }, []);

  // Load contracts from Firestore
  useEffect(() => {
    if (!user) return;

    const contractsQuery = query(collection(db, 'contracts'));
    const unsubscribeContracts = onSnapshot(contractsQuery, (snapshot) => {
      const list: Contract[] = [];
      snapshot.forEach(doc => {
        list.push({ id: doc.id, ...doc.data() } as Contract);
      });
      setContracts(list);
      setLoadingContracts(false);
    }, (error) => {
      console.error("Firestore loading contracts error in StateContracts:", error);
      handleFirestoreError(error, OperationType.LIST, 'contracts');
      setLoadingContracts(false);
    });

    return () => unsubscribeContracts();
  }, [user]);

  // Filters logic
  const filteredContracts = contracts.filter(c => {
    if (!c) return false;
    const matchesState = contractStates.length === 0 || (c.state && contractStates.includes(c.state));
    const matchesNaics = contractNaicsCodes.length === 0 || (c.naics && contractNaicsCodes.includes(c.naics));
    const matchesSearch = !contractSearch.trim() || 
      (c.title || '').toLowerCase().includes(contractSearch.toLowerCase()) ||
      (c.agency || '').toLowerCase().includes(contractSearch.toLowerCase()) ||
      (c.awardee || '').toLowerCase().includes(contractSearch.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(contractSearch.toLowerCase());
    return matchesState && matchesNaics && matchesSearch;
  });

  const handleSelectContract = (contract: Contract) => {
    setSelectedContractId(contract.contractId);
    localStorage.setItem('rfx_selected_contract_id', contract.contractId);
    localStorage.setItem('rfx_selected_contract_title', contract.title);
    
    // Dispatch custom event to notify other parts of the app if needed
    window.dispatchEvent(new CustomEvent('rfx_contract_selected', { detail: contract }));
    
    // Show user a non-intrusive confirmation
    const toastEvent = new CustomEvent('add-toast', {
      detail: {
        id: Math.random().toString(),
        type: 'success',
        title: 'Contract Applied as Knowledge Source',
        message: `"${contract.title}" has been selected. You can now use it in the AI proposal drafting engine under My RFX Responses.`
      }
    });
    window.dispatchEvent(toastEvent);
  };

  // Merge NAICS codes from the contracts with the vendor profile NAICS codes
  const profileNaics = vendorProfile?.naicsCodes || [];
  const contractNaicsListLocal = contracts.map(c => c.naics).filter(Boolean);
  const combinedNaics = Array.from(new Set([...profileNaics, ...contractNaicsListLocal])).sort();

  const stateOptions = STATES_LIST.map(st => ({ value: st.code, label: `${st.name} (${st.code})` }));

  const naicsOptions = combinedNaics.map(nc => {
    const found = COMPLETE_NAICS_CODES.find(item => item.code === nc);
    return {
      value: nc,
      label: found ? `${nc} - ${found.title}` : nc
    };
  });

  return (
    <div className="space-y-6">
      {/* Search and Filters Bar */}
      <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-2xs space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by awardee name, contract title, keyword..."
              value={contractSearch}
              onChange={(e) => setContractSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 focus:border-indigo-500 rounded-xl text-sm font-sans text-gray-800 placeholder-gray-400 focus:outline-none transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2.5 items-center">
            <div className="w-48">
              <MultiSelectCombo
                options={stateOptions}
                selectedValues={contractStates}
                onChange={setContractStates}
                placeholder="All States"
                icon={<MapPin className="w-3.5 h-3.5" />}
              />
            </div>
            <div className="w-64">
              <MultiSelectCombo
                options={naicsOptions}
                selectedValues={contractNaicsCodes}
                onChange={setContractNaicsCodes}
                placeholder="All NAICS"
                icon={<Database className="w-3.5 h-3.5" />}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contracts Grid */}
      {loadingContracts ? (
        <div className="p-16 text-center space-y-3 bg-white border border-gray-100 rounded-2xl shadow-2xs">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-500 font-medium">Accessing public awarded contracts database...</p>
        </div>
      ) : filteredContracts.length === 0 ? (
        <div className="p-16 text-center bg-white border border-gray-100 rounded-2xl shadow-2xs space-y-3">
          <Database className="w-12 h-12 text-gray-300 mx-auto" />
          <div className="space-y-1">
            <p className="text-base font-bold text-gray-800">No contracts found</p>
            <p className="text-xs text-gray-400">Try adjusting your state or keyword filters to broaden your search.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredContracts.map((contract) => {
            const isSelected = selectedContractId === contract.contractId;
            return (
              <div
                key={contract.contractId}
                className={`p-5 bg-white border rounded-2xl shadow-2xs hover:shadow-xs transition-all flex flex-col justify-between text-left relative overflow-hidden group ${
                  isSelected 
                    ? 'border-indigo-500 ring-2 ring-indigo-500/10' 
                    : 'border-gray-100'
                }`}
              >
                {/* Header info */}
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="space-y-0.5 min-w-0">
                      <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-gray-400 block">
                        {contract.contractId}
                      </span>
                      <h4 className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug group-hover:text-indigo-600 transition-colors">
                        {contract.title}
                      </h4>
                    </div>
                    <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      isSelected 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                        : 'bg-indigo-50 text-indigo-700 border border-indigo-100'
                    }`}>
                      {isSelected ? 'Active Source' : 'Awarded'}
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 mb-4 line-clamp-3 leading-relaxed">
                    {contract.description}
                  </p>
                </div>

                {/* Bottom metadata */}
                <div className="pt-3.5 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2.5 text-xs font-medium text-gray-500">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100 text-gray-600">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" /> {contract.state}
                    </span>
                    <span className="text-gray-700 font-semibold">{contract.awardee}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-600 font-extrabold font-mono text-sm">
                      ${contract.value?.toLocaleString()}
                    </span>
                    <button
                      onClick={() => setViewingContract(contract)}
                      className="text-indigo-600 hover:text-indigo-800 font-bold transition-all cursor-pointer inline-flex items-center gap-0.5 hover:underline text-xs"
                    >
                      Details <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Apply button hover element */}
                <div className="mt-4 pt-3 border-t border-dashed border-gray-100 flex justify-end">
                  <button
                    onClick={() => handleSelectContract(contract)}
                    className={`w-full py-2 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      isSelected 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                        : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-100'
                    }`}
                  >
                    <Award className="w-3.5 h-3.5" />
                    {isSelected ? 'Selected as Drafting Reference' : 'Use as Drafting Reference'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Award Detail Modal */}
      <AnimatePresence>
        {viewingContract && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-xs p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-2xl bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              {/* Header */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-150 flex items-center justify-between text-left">
                <div>
                  <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full uppercase tracking-wider">
                    Awarded Contract Details
                  </span>
                  <h3 className="text-base font-extrabold text-gray-950 mt-1.5 leading-snug">
                    {viewingContract.contractId} — {viewingContract.title}
                  </h3>
                </div>
                <button
                  onClick={() => setViewingContract(null)}
                  className="p-1.5 hover:bg-gray-200 text-gray-400 hover:text-gray-600 rounded-xl transition cursor-pointer"
                >
                  <X className="w-4.5 h-4.5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto space-y-6 text-left font-sans text-xs text-gray-700">
                {/* Meta Card */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-gray-50 p-4.5 rounded-xl border border-gray-100">
                  <div>
                    <span className="text-[10px] text-gray-400 font-mono font-bold block mb-0.5">AWARD VALUE</span>
                    <strong className="text-emerald-700 font-mono text-sm">${viewingContract.value?.toLocaleString()}</strong>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-mono font-bold block mb-0.5">WINNING AWARDEE</span>
                    <strong className="text-gray-800 text-xs">{viewingContract.awardee}</strong>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-mono font-bold block mb-0.5">AGENCY</span>
                    <strong className="text-gray-800 text-xs">{viewingContract.agency}</strong>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400 font-mono font-bold block mb-0.5">PERIOD OF PERFORMANCE</span>
                    <strong className="text-gray-800 text-xs">{viewingContract.effectiveDate} - {viewingContract.expirationDate}</strong>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                  <h4 className="font-bold text-gray-900 uppercase font-mono text-[10px] tracking-wider text-gray-500">Scope of Work & Deliverables</h4>
                  <p className="leading-relaxed bg-gray-50/50 p-4 rounded-xl border border-gray-100 text-xs text-gray-600">
                    {viewingContract.description}
                  </p>
                </div>

                {/* Winning Excerpt */}
                <div className="space-y-1.5">
                  <h4 className="font-bold text-gray-900 uppercase font-mono text-[10px] tracking-wider text-indigo-500">Winning Proposal Excerpt (Style Reference)</h4>
                  <div className="bg-indigo-50/30 border border-indigo-100 p-4 rounded-xl relative">
                    <span className="absolute right-3.5 top-3.5 text-indigo-400 opacity-20">
                      <Award className="w-10 h-10" />
                    </span>
                    <p className="leading-relaxed text-gray-700 italic text-xs">
                      "{viewingContract.winningProposalExcerpt}"
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <span className="text-[10px] text-gray-400 font-mono font-bold">NAICS: {viewingContract.naics} | State: {viewingContract.state}</span>
                <button
                  onClick={() => {
                    handleSelectContract(viewingContract);
                    setViewingContract(null);
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-xs font-bold text-white rounded-xl cursor-pointer transition-colors"
                >
                  Apply as Knowledge Source
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
